import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FilterX } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

const getFilterValue = (columnFilters, filterId) =>
  columnFilters.find((filter) => filter.id === filterId)?.value || 'all';

const AdminStocksFilters = ({ brandOptions, columnFilters, serviceDeskOptions, table }) => {
  const { t } = useTranslation();
  const serviceDeskFilter = getFilterValue(columnFilters, 'serviceDeskName');
  const brandFilter = getFilterValue(columnFilters, 'brand');
  const stockStatusFilter = getFilterValue(columnFilters, 'stockStatus');
  const hasActiveFilters =
    serviceDeskFilter !== 'all' ||
    brandFilter !== 'all' ||
    stockStatusFilter !== 'all';

  return (
    <>
      <Select
        value={serviceDeskFilter}
        onValueChange={(value) => {
          table.getColumn('serviceDeskName')?.setFilterValue(
            value === 'all' ? undefined : value
          );
        }}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder={t('All service desks')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('All service desks')}</SelectItem>
          {serviceDeskOptions.map((serviceDesk) => (
            <SelectItem key={serviceDesk.id} value={serviceDesk.name}>
              {serviceDesk.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={brandFilter}
        onValueChange={(value) => {
          table.getColumn('brand')?.setFilterValue(value === 'all' ? undefined : value);
        }}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder={t('All brands')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('All brands')}</SelectItem>
          {brandOptions.map((brand) => (
            <SelectItem key={brand.id} value={brand.label}>
              {brand.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={stockStatusFilter}
        onValueChange={(value) => {
          table.getColumn('stockStatus')?.setFilterValue(
            value === 'all' ? undefined : value
          );
        }}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder={t('All statuses')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('All statuses')}</SelectItem>
          <SelectItem value="Available">{t('Available')}</SelectItem>
          <SelectItem value="Low stock">{t('Low stock')}</SelectItem>
          <SelectItem value="Out of stock">{t('Out of stock')}</SelectItem>
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button
          onClick={() => table.resetColumnFilters()}
          variant="outline"
          className="flex items-center gap-2 whitespace-nowrap"
        >
          <FilterX className="h-4 w-4" />
          {t('Clear filters')}
        </Button>
      )}
    </>
  );
};

AdminStocksFilters.propTypes = {
  brandOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  columnFilters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.any,
    })
  ).isRequired,
  serviceDeskOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  table: PropTypes.shape({
    getColumn: PropTypes.func.isRequired,
    resetColumnFilters: PropTypes.func.isRequired,
  }).isRequired,
};

export { AdminStocksFilters };
